import React from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import request from "../../utilities/requests";
import { toast } from "../../utilities/SnackBarUtils";
import { subscribeToChat } from "./chatSocket";

const REFRESH_MS = 60000;

const useChatSummary = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [summary, setSummary] = React.useState({ available: false, unread: 0 });
  const unreadRef = React.useRef(0);
  const pathRef = React.useRef(location.pathname);
  const lastLoadRef = React.useRef(0);

  const load = React.useCallback(
    async ({ announce = false } = {}) => {
      lastLoadRef.current = Date.now();
      const result = await request("eventchat/summary");
      if (!result?.success) return;

      const unread = result.unread || 0;
      if (
        announce &&
        unread > unreadRef.current &&
        !pathRef.current.startsWith("/chats")
      ) {
        toast.info(t("chat.toast.new"));
      }
      unreadRef.current = unread;
      setSummary({ available: !!result.available, unread });
    },
    [t]
  );

  React.useEffect(() => {
    pathRef.current = location.pathname;
    if (Date.now() - lastLoadRef.current > 5000) load();
  }, [location.pathname, load]);

  React.useEffect(() => {
    const intervalId = setInterval(() => load(), REFRESH_MS);
    const unsubscribe = subscribeToChat({
      onMessage: () => load({ announce: true }),
      onUpdated: () => load(),
      onMatched: () => {
        toast.success(t("chat.toast.match"));
        load();
      },
      onConnect: () => load(),
    });
    return () => {
      clearInterval(intervalId);
      unsubscribe();
    };
  }, [load, t]);

  return summary;
};

export default useChatSummary;
